import fs from "fs";
import path from "path";

import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Text,
} from "@chakra-ui/react";
import { GetStaticProps } from "next";
import NextHead from "next/head";
import { FC, useState } from "react";
import rehypeStringify from "rehype-stringify";
import remarkGfm from "remark-gfm";
import remarkParse from "remark-parse";
import remarkRehype from "remark-rehype";
import { unified } from "unified";

import { getExtensions } from "api/get-extensions";
import { getIntegrations } from "api/get-integrations";
import { Footer } from "components/footer";
import { Head } from "components/head";
import { Main } from "components/layout/main";
import { PageProps } from "pages/_app";
import { Heading } from "ui";
import { LinkIcon } from "ui/icons";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tag = "new" | "improvement";

/** A single list-item entry parsed from the changelog markdown. */
interface ChangelogItem {
  tag: Tag;
  html: string;
}

/** A product-area group (h3) containing one or more items. */
interface ChangelogSection {
  area: string;
  items: ChangelogItem[];
}

/** A time-period group (h2) containing product-area sections. */
interface ChangelogPeriod {
  period: string; // e.g. "May 2026"
  sections: ChangelogSection[];
}

// ---------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------

const TAG_COLORS: Record<Tag, { bg: string; color: string }> = {
  new: { bg: "primary.background", color: "primary.base" },
  improvement: { bg: "space.100", color: "space.700" },
};

const TAG_LABELS: Record<Tag, string> = {
  new: "New",
  improvement: "Improved",
};

const ALL_TAGS: Tag[] = ["new", "improvement"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Turn "May 2026" into "may-2026" for use as an anchor ID. */
const toSlug = (period: string) =>
  period.toLowerCase().replace(/\s+/g, "-");

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

const TagBadge: FC<{ tag: Tag }> = ({ tag }) => (
  <Badge
    bg={TAG_COLORS[tag]?.bg || "base.background"}
    color={TAG_COLORS[tag]?.color || "text.secondary"}
    w="66px"
    px={0}
    py={0.5}
    borderRadius="md"
    fontSize="xs"
    fontWeight="semibold"
    textTransform="capitalize"
    textAlign="center"
    flexShrink={0}
  >
    {TAG_LABELS[tag] || tag}
  </Badge>
);

const EntryItem: FC<{ item: ChangelogItem }> = ({ item }) => (
  <Flex as="li" gap={3} alignItems="baseline" mb={3} _last={{ mb: 0 }}>
    <TagBadge tag={item.tag} />
    <Box
      fontSize="md"
      color="text.primary"
      lineHeight="tall"
      sx={{
        "& p": { display: "inline", mb: 0 },
        "& strong": { color: "text.primary", fontWeight: "semibold" },
        "& a": { color: "link.default", textDecoration: "underline" },
        "& a:hover": { color: "link.hover" },
        "& code": {
          bg: "base.background",
          px: 1.5,
          py: 0.5,
          borderRadius: "sm",
          fontSize: "sm",
        },
        "& img": {
          display: "block",
          maxWidth: "100%",
          borderRadius: "md",
          mt: 2,
          border: "1px solid",
          borderColor: "base.divider",
        },
      }}
      // Safe: HTML is generated at build time from our own changelog.md via
      // remark/rehype — never from user input.
      dangerouslySetInnerHTML={{ __html: item.html }}
    />
  </Flex>
);

const AreaSection: FC<{ section: ChangelogSection }> = ({ section }) => (
  <Box mb={5}>
    <Text
      as="h3"
      fontSize="xs"
      fontWeight="medium"
      color="text.secondary"
      textTransform="uppercase"
      letterSpacing="wider"
      mb={2}
    >
      {section.area}
    </Text>
    <Box as="ul" listStyleType="none" pl={0}>
      {section.items.map((item, i) => (
        <EntryItem key={i} item={item} />
      ))}
    </Box>
  </Box>
);

const PeriodBlock: FC<{
  period: ChangelogPeriod;
  isFirst?: boolean;
}> = ({ period, isFirst }) => {
  const slug = toSlug(period.period);

  const headerLink = (
    <Link
      href={`#${slug}`}
      display="flex"
      alignItems="center"
      fontSize="sm"
      fontWeight="medium"
      color="text.primary"
      _hover={{ textDecoration: "none" }}
    >
      {period.period}
      <LinkIcon
        boxSize={4}
        ml={2}
        color="primary.base"
        opacity={0}
        transition="150ms opacity"
        _groupHover={{ opacity: 1 }}
      />
    </Link>
  );

  return (
    <Box
      id={slug}
      pt={isFirst ? 0 : 8}
      borderTop={isFirst ? "none" : "1px solid"}
      borderColor="base.divider"
      scrollMarginTop="100px"
    >
      <Flex gap={6}>
        <Box
          w="120px"
          flexShrink={0}
          pt={isFirst ? 0 : 1}
          display={["none", "none", "block"]}
        >
          <Text
            as="h2"
            className="group"
            pos="sticky"
            top="calc(var(--theme-sizes-header) + 1.5rem)"
          >
            {headerLink}
          </Text>
        </Box>
        <Box flex={1} pt={isFirst ? 0 : 1}>
          <Text as="h2" className="group" mb={4} display={["block", "block", "none"]}>
            {headerLink}
          </Text>
          {period.sections.map((section) => (
            <AreaSection key={section.area} section={section} />
          ))}
        </Box>
      </Flex>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");

const Changelog: FC<PageProps & { periods: ChangelogPeriod[] }> = ({
  periods,
}) => {
  const [activeTag, setActiveTag] = useState<Tag | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleMonths, setVisibleMonths] = useState(3);

  // Filter periods → sections → items by tag and search query
  const filtered: ChangelogPeriod[] = periods
    .map((p) => ({
      ...p,
      sections: p.sections
        .map((s) => ({
          ...s,
          items: s.items.filter((item) => {
            const matchesTag = !activeTag || item.tag === activeTag;
            if (!matchesTag) return false;
            if (!searchQuery.trim()) return true;
            const query = searchQuery.toLowerCase();
            const text = stripHtml(item.html).toLowerCase();
            const area = s.area.toLowerCase();
            return text.includes(query) || area.includes(query);
          }),
        }))
        .filter((s) => s.items.length > 0),
    }))
    .filter((p) => p.sections.length > 0);

  return (
    <>
      <Head
        title="Changelog"
        description="See what's new in Hightouch, including product updates, new integrations, and platform improvements. Updated weekly."
      />
      <NextHead>
        <link
          rel="alternate"
          type="application/rss+xml"
          title="Hightouch Changelog"
          href="/docs/changelog/feed.xml"
        />
      </NextHead>
      <Flex width="100%" px={{ base: 4, md: 8, lg: 12 }}>
        <Main maxWidth="900px">
          <Box as="article">
            <Heading as="h1">What's new</Heading>
            <Flex
              align="center"
              justify="space-between"
              mt={2}
              mb={4}
              flexWrap="wrap"
              gap={3}
            >
              <Text color="text.secondary" mb={0}>
                Product updates, new integrations, and platform
                improvements.
              </Text>
              <HStack spacing={3} flexShrink={0}>
                <Link
                  href="/docs/changelog/feed.xml"
                  color="text.tertiary"
                  fontSize="sm"
                  fontWeight="medium"
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  _hover={{ color: "text.primary" }}
                  isExternal
                >
                  <Box
                    as="svg"
                    width="14px"
                    height="14px"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 11a9 9 0 0 1 9 9" />
                    <path d="M4 4a16 16 0 0 1 16 16" />
                    <circle cx="5" cy="19" r="1" />
                  </Box>
                  RSS
                </Link>
              </HStack>
            </Flex>

            <Flex align="center" gap={3} mb={6}>
              <Flex align="center" gap={1.5} flexShrink={0}>
                {[null, ...ALL_TAGS].map((tag) => {
                  const isActive = activeTag === tag;
                  const label = tag ? TAG_LABELS[tag] : "All";
                  return (
                    <Box
                      key={label}
                      as="button"
                      px={3}
                      py={1}
                      borderRadius="lg"
                      fontSize="sm"
                      fontWeight="medium"
                      cursor="pointer"
                      transition="background-color 150ms ease-out, color 150ms ease-out"
                      bg={isActive ? "primary.base" : "base.background"}
                      color={isActive ? "white" : "text.secondary"}
                      border="none"
                      _hover={{
                        bg: isActive ? "primary.hover" : "base.divider",
                        color: isActive ? "white" : "text.primary",
                      }}
                      _focusVisible={{
                        outline: "2px solid",
                        outlineColor: "primary.base",
                        outlineOffset: "2px",
                      }}
                      onClick={() =>
                        setActiveTag(tag === activeTag ? null : tag)
                      }
                    >
                      {label}
                    </Box>
                  );
                })}
              </Flex>

              <InputGroup size="md" flex={1}>
                <InputLeftElement h="100%" w="36px" pl={3} pointerEvents="none">
                  <Box
                    as="svg"
                    width="15px"
                    height="15px"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    color="text.tertiary"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </Box>
                </InputLeftElement>
                <Input
                  placeholder="Search changelog..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  borderRadius="lg"
                  borderColor="base.border"
                  pl={10}
                  fontSize="md"
                  fontFamily="inherit"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{
                    borderColor: "primary.base",
                    boxShadow: "0 0 0 1px var(--theme-colors-primary-base)",
                  }}
                  _placeholder={{ color: "text.placeholder" }}
                />
              </InputGroup>
            </Flex>

            <Box
              borderTop="1px solid"
              borderColor="base.divider"
              pt={6}
            >
            {(() => {
              // Show all results when searching or filtering; otherwise cap to visibleMonths
              const isFiltering = activeTag !== null || searchQuery.trim() !== "";
              const visible = isFiltering
                ? filtered
                : filtered.slice(0, visibleMonths);
              const hasMore = !isFiltering && filtered.length > visibleMonths;

              return (
                <>
                  {visible.map((period, i) => (
                    <PeriodBlock
                      key={period.period}
                      period={period}
                      isFirst={i === 0}
                    />
                  ))}

                  {hasMore && (
                    <Flex
                      direction="column"
                      align="center"
                      gap={2}
                      pt={6}
                      pb={2}
                    >
                      <Text fontSize="sm" color="text.tertiary">
                        Showing {visibleMonths} of {filtered.length} months
                      </Text>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setVisibleMonths((v) => v + 6)}
                        fontWeight="medium"
                      >
                        Show older updates
                      </Button>
                    </Flex>
                  )}

                  {filtered.length === 0 && (
                    <Box py={12} textAlign="center">
                      <Text color="text.tertiary" mb={3}>
                        {searchQuery.trim()
                          ? `No entries match "${searchQuery}".`
                          : "No changelog entries found for this filter."}
                      </Text>
                      <Button
                        size="sm"
                        variant="ghost"
                        color="link.default"
                        fontWeight="medium"
                        _hover={{ bg: "base.background" }}
                        onClick={() => {
                          setActiveTag(null);
                          setSearchQuery("");
                        }}
                      >
                        Clear filters
                      </Button>
                    </Box>
                  )}
                </>
              );
            })()}
            </Box>
          </Box>

        </Main>
      </Flex>

      <Footer
        align="flex-start"
        contentWidth="900px"
        px={{ base: 4, md: 8, lg: 12 }}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Parser — turns the single changelog.md into structured data
// ---------------------------------------------------------------------------

/**
 * Parses the changelog markdown file into a structured hierarchy:
 *   h2 (period) → h3 (product area) → list items (entries)
 *
 * Each list item is expected to start with a tag marker:
 *   - **[New]** ...
 *   - **[Improvement]** ...
 *   - **[Fix]** ...
 */
async function parseChangelog(
  raw: string,
): Promise<ChangelogPeriod[]> {
  const processor = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeStringify, { allowDangerousHtml: true });

  const lines = raw.split("\n");
  const periods: ChangelogPeriod[] = [];
  let currentPeriod: ChangelogPeriod | null = null;
  let currentSection: ChangelogSection | null = null;

  // Accumulates multi-line list items
  let currentItemLines: string[] = [];

  const TAG_PATTERN = /^\*\*\[(New|Improvement|Fix)\]\*\*/i;

  async function flushItem() {
    if (currentItemLines.length === 0 || !currentSection) return;

    const text = currentItemLines.join("\n").trim();
    currentItemLines = [];

    // Extract tag from **[Tag]** prefix
    const tagMatch = text.match(TAG_PATTERN);
    if (!tagMatch) return;

    const tag = tagMatch[1].toLowerCase() as Tag;
    // Remove the tag prefix and any inline date comment from rendered content
    const body = text
      .replace(TAG_PATTERN, "")
      .replace(/<!--\s*\d{4}-\d{2}-\d{2}\s*-->/, "")
      .trim();

    const result = await processor.process(body);
    // Prepend /docs basePath to internal links (href="/..." but not href="//..." or href="http")
    const html = String(result).replace(
      /href="\/(?!\/|docs\/)/g,
      'href="/docs/',
    );
    currentSection.items.push({ tag, html });
  }

  for (const line of lines) {
    if (line.startsWith("## ")) {
      await flushItem();
      currentSection = null;
      currentPeriod = {
        period: line.replace("## ", "").trim(),
        sections: [],
      };
      periods.push(currentPeriod);
    } else if (line.startsWith("### ")) {
      await flushItem();
      if (!currentPeriod) continue;
      currentSection = {
        area: line.replace("### ", "").trim(),
        items: [],
      };
      currentPeriod.sections.push(currentSection);
    } else if (line.startsWith("- ")) {
      // New list item — flush the previous one
      await flushItem();
      currentItemLines.push(line.replace(/^- /, ""));
    } else if (
      line.startsWith("  ") &&
      currentItemLines.length > 0
    ) {
      // Continuation of a multi-line list item
      currentItemLines.push(line.trim());
    }
    // Skip other lines (h1 title, blank lines, prose intro)
  }
  // Flush last item
  await flushItem();

  return periods;
}

// ---------------------------------------------------------------------------
// Data fetching
// ---------------------------------------------------------------------------

export const getStaticProps: GetStaticProps = async () => {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();

  const changelogPath = path.join(
    process.cwd(),
    "src/content/changelog.md",
  );

  let periods: ChangelogPeriod[] = [];

  if (fs.existsSync(changelogPath)) {
    const raw = fs.readFileSync(changelogPath, "utf-8");
    periods = await parseChangelog(raw);
  }

  return {
    props: {
      ...integrations,
      extensions,
      periods,
      meta: {
        title: "Changelog",
        description:
          "See what's new in Hightouch, including product updates, new integrations, and platform improvements. Updated weekly.",
        toc: [],
      },
    },
    revalidate: 60,
  };
};

export default Changelog;
